import { RemoveIcon } from '@diligentcorp/atlas-react-icons';
import { GetIssueRelationshipsResponseDto } from '@issuemanager/api-client';
import { useLocalizedRelationshipTypes } from '@issuemanager/client/features/issues';
import {
  IconButton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(1),
  boxShadow: '0px -1px 0px 0px #E6E6E6 inset',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    backgroundColor: '#EBEEF2',
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    '&:nth-of-type(even)': {
      backgroundColor: '#EBEEF2',
    },
  },
}));

type RelationshipsTableProps = {
  relationships: GetIssueRelationshipsResponseDto[];
  onRemoveClick: (relationship: GetIssueRelationshipsResponseDto) => void;
};

export function RelationshipsTable({
  relationships,
  onRemoveClick,
}: RelationshipsTableProps): JSX.Element {
  const { t } = useTranslation(['issue']);
  const { getRelationshipTypeLabel } = useLocalizedRelationshipTypes();

  return (
    <StyledTableContainer>
      <StyledTable data-testid='relationships-table'>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography fontWeight={600}>
                {t('issue:relationships.table.id')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography fontWeight={600}>
                {t('issue:relationships.table.type')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography fontWeight={600}>
                {t('issue:relationships.table.name')}
              </Typography>
            </TableCell>
            <TableCell align='right' sx={{ width: '50px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {relationships.map((relationship) => (
            <TableRow
              key={relationship.relatedObject.id}
              data-testid={`relationship-row-${relationship.relatedObject.id}`}
            >
              <TableCell>
                {/* TODO: https://diligentbrands.atlassian.net/browse/RCP-33660 */}
                {/* <Link
                    href={url}
                    underline='hover'
                    color='primary'
                    data-testid={`relationship-id-link-${relationship.relatedObject.id}`}
                  >
                    {relationship.relatedObject.id}
                  </Link> */}
                {relationship.relatedObject.id}
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {getRelationshipTypeLabel(relationship.relatedObject.type)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {relationship.relatedObject.name}
                </Typography>
              </TableCell>
              <TableCell align='right'>
                <IconButton
                  onClick={() => {
                    onRemoveClick(relationship);
                  }}
                  aria-label={t('issue:relationships.actions.remove')}
                  data-testid={`remove-relationship-${relationship.relatedObject.id}`}
                >
                  <RemoveIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
}
